/**
 * Bulk lifecycle action bar for bank items — appears when ≥1 item selected.
 * Each action shows how many of the selected items are eligible for that
 * transition; confirmation dialog before executing.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { NEXT_STATUS } from './banksMeta';
import { Loader2, X, Send, ThumbsUp, Rocket, Archive } from 'lucide-react';

const ACTIONS = [
  { target: 'in_review', label: 'Submit for review', icon: Send },
  { target: 'approved',  label: 'Approve',           icon: ThumbsUp },
  { target: 'published', label: 'Publish',           icon: Rocket },
  { target: 'retired',   label: 'Retire',            icon: Archive, danger: true },
];

export function BankBulkActionBar({ bankId, selectedItems, onDone, onClear }) {
  const { patch } = useApi();
  const [pending, setPending] = useState(null); // action being confirmed
  const [running, setRunning] = useState(false);

  const eligibleFor = (target) =>
    selectedItems.filter(it => (NEXT_STATUS[it.status] || []).includes(target));

  const run = async () => {
    const action = pending;
    setRunning(true);
    try {
      const res = await patch(`/question-banks/${bankId}/items/bulk-lifecycle`, {
        item_ids: selectedItems.map(it => it.item_id),
        target_status: action.target,
      });
      if (res.succeeded > 0 && res.failed === 0) {
        toast.success(`${res.succeeded} question(s) moved to ${action.target.replace('_', ' ')}.`);
      } else if (res.succeeded > 0) {
        toast.warning(`${res.succeeded} moved to ${action.target.replace('_', ' ')} · ${res.failed} skipped (not eligible).`);
      } else {
        toast.error('No questions were eligible for that action.');
      }
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setRunning(false);
      setPending(null);
    }
  };

  if (selectedItems.length === 0) return null;

  return (
    <div
      className="sticky bottom-3 z-20 rounded-xl border bg-card shadow-lg px-3 py-2 flex flex-wrap items-center gap-2"
      data-testid="bulk-action-bar"
    >
      <span className="text-sm font-medium" data-testid="bulk-selected-count">
        {selectedItems.length} selected
      </span>
      <div className="flex flex-wrap gap-1.5 flex-1">
        {ACTIONS.map(a => {
          const n = eligibleFor(a.target).length;
          const Icon = a.icon;
          return (
            <Button
              key={a.target}
              variant="outline"
              size="sm"
              className={`h-8 text-xs ${a.danger ? 'text-red-500 border-red-200 hover:bg-red-50' : ''}`}
              disabled={n === 0}
              onClick={() => setPending({ ...a, eligible: n })}
              data-testid={`bulk-${a.target}-btn`}
              title={n === 0 ? 'None of the selected questions can make this transition' : ''}
            >
              <Icon className="w-3.5 h-3.5 mr-1" />{a.label} ({n})
            </Button>
          );
        })}
      </div>
      <Button variant="ghost" size="sm" className="h-8" onClick={onClear} data-testid="bulk-clear-btn">
        <X className="w-4 h-4 mr-1" />Clear
      </Button>

      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.label} {pending?.eligible} question{pending?.eligible === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.eligible} of the {selectedItems.length} selected question(s) will move to{' '}
              <span className="font-medium">{pending?.target?.replace('_', ' ')}</span>.
              {pending && pending.eligible < selectedItems.length &&
                ` The other ${selectedItems.length - pending.eligible} are not in an eligible state and will be skipped.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={running}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={running} data-testid="bulk-confirm-btn">
              {running && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
